import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../../hooks/use-auth";

// Paleta da marca Centro Derma (extraída da logo)
const C = {
	bgDark: "#0f2b27",
	bgMid: "#1a3d38",
	bgLight: "#1f4a44",
	primary: "#3fa08a",
	primaryLight: "#5bbaa5",
	white: "#ffffff",
	whiteAlpha75: "rgba(255,255,255,0.75)",
	whiteAlpha35: "rgba(255,255,255,0.35)",
	primaryAlpha25: "rgba(63,160,138,0.25)",
};

export default function LoginPage() {
	const { login, register, isLoading } = useAuth();
	const location = useLocation();
	const navigate = useNavigate();
	const successMessage = location.state?.message;
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [showRegister, setShowRegister] = useState(false);
	const [showForgotPassword, setShowForgotPassword] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showRegisterPassword, setShowRegisterPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const termsId = useId();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		if (!email || !password) {
			setError("Email e senha são obrigatórios.");
			return;
		}
		try {
			await login(email, password);
			navigate("/dashboard");
		} catch (err) {
			setError(
				err instanceof Error
					? err.message || "Erro ao fazer login."
					: "Erro ao fazer login. Tente novamente.",
			);
		}
	};

	const handleRegister = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		const form = e.target as HTMLFormElement;
		const name = (form.elements.namedItem("name") as HTMLInputElement).value;
		const email = (form.elements.namedItem("email") as HTMLInputElement).value;
		const password = (form.elements.namedItem("password") as HTMLInputElement).value;
		const confirmPassword = (form.elements.namedItem("confirmPassword") as HTMLInputElement).value;
		if (!name || !email || !password || !confirmPassword) {
			setError("Todos os campos são obrigatórios.");
			return;
		}
		if (password !== confirmPassword) {
			setError("As senhas não coincidem.");
			return;
		}
		if (password.length < 6) {
			setError("A senha deve ter no mínimo 6 caracteres.");
			return;
		}
		try {
			await register(name, email, password);
			navigate("/dashboard");
		} catch (err) {
			setError(
				err instanceof Error ? err.message || "Erro ao criar conta." : "Erro ao criar conta.",
			);
		}
	};

	const handleForgotPassword = (e: React.FormEvent) => {
		e.preventDefault();
		alert("Link de recuperação será enviado para seu email!");
		setShowForgotPassword(false);
	};

	// Estilos compartilhados dos inputs
	const inputClass =
		"w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 transition-all outline-none focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-100";

	// Painel esquerdo com identidade da clínica
	const BrandPanel = () => (
		<div
			className="hidden lg:flex lg:w-[45%] relative overflow-hidden flex-col items-center justify-center p-12 flex-shrink-0"
			style={{
				background: `linear-gradient(160deg, ${C.bgLight} 0%, ${C.bgMid} 45%, ${C.bgDark} 100%)`,
			}}
		>
			{/* Decorações de fundo */}
			<div className="absolute inset-0 pointer-events-none">
				<svg
					className="absolute top-0 right-0 opacity-10"
					width="320"
					height="320"
					viewBox="0 0 320 320"
					fill="none"
				>
					<circle cx="280" cy="40" r="160" fill={C.primary} />
				</svg>
				<svg
					className="absolute bottom-0 left-0 opacity-10"
					width="280"
					height="280"
					viewBox="0 0 280 280"
					fill="none"
				>
					<circle cx="0" cy="280" r="140" fill={C.primary} />
				</svg>
				<svg
					className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-5"
					width="500"
					height="500"
					viewBox="0 0 500 500"
					fill="none"
				>
					<circle cx="250" cy="250" r="240" stroke={C.primary} strokeWidth="1.5" />
					<circle cx="250" cy="250" r="180" stroke={C.primary} strokeWidth="1" />
					<circle cx="250" cy="250" r="120" stroke={C.primary} strokeWidth="1" />
				</svg>
				{/* Folhas decorativas */}
				{[
					{ cls: "absolute top-16 left-8 opacity-25", w: 60, h: 80 },
					{ cls: "absolute top-24 left-20 opacity-15", w: 40, h: 55 },
					{ cls: "absolute bottom-20 right-12 opacity-20", w: 50, h: 70 },
					{ cls: "absolute bottom-36 right-28 opacity-15", w: 35, h: 48 },
				].map(({ cls, w, h }, i) => (
					<svg key={i} className={cls} width={w} height={h} viewBox="0 0 60 80" fill="none">
						<path
							d="M30 80 C30 80 0 50 0 30 C0 13 13 0 30 0 C47 0 60 13 60 30 C60 50 30 80 30 80Z"
							fill={i % 2 === 0 ? C.primary : "#2d7a6b"}
						/>
						{i === 0 && (
							<>
								<line x1="30" y1="10" x2="30" y2="70" stroke="#e6f4f1" strokeWidth="1.5" />
								<line x1="30" y1="30" x2="15" y2="20" stroke="#e6f4f1" strokeWidth="1" />
								<line x1="30" y1="40" x2="45" y2="30" stroke="#e6f4f1" strokeWidth="1" />
							</>
						)}
						{i === 2 && <line x1="30" y1="10" x2="30" y2="70" stroke="#e6f4f1" strokeWidth="1.5" />}
					</svg>
				))}
				{/* Pontos */}
				{[
					{ cls: "absolute top-32 right-16 w-3 h-3", opacity: 0.4 },
					{ cls: "absolute top-48 right-32 w-2 h-2", opacity: 0.25 },
					{ cls: "absolute bottom-40 left-20 w-4 h-4", opacity: 0.3 },
					{ cls: "absolute bottom-28 left-40 w-2 h-2", opacity: 0.2 },
				].map(({ cls, opacity }, i) => (
					<div
						key={i}
						className={`${cls} rounded-full`}
						style={{ backgroundColor: C.primary, opacity }}
					/>
				))}
			</div>

			{/* Conteúdo central */}
			<div className="relative z-10 text-center">
				{/* Logo */}
				<div className="flex justify-center mb-8">
					<div className="w-40 h-40 rounded-2xl overflow-hidden shadow-2xl bg-white p-3">
						<img src="/logo.jpeg" alt="Centro Derma" className="w-full h-full object-contain" />
					</div>
				</div>

				{/* Separador */}
				<div className="flex items-center justify-center gap-3 mb-5">
					<div className="h-px w-10" style={{ backgroundColor: C.primary }} />
					<span
						className="text-xs font-medium uppercase tracking-widest"
						style={{ color: C.primaryLight }}
					>
						Dermatologia & Estética
					</span>
					<div className="h-px w-10" style={{ backgroundColor: C.primary }} />
				</div>

				<p
					className="text-lg font-light leading-relaxed max-w-xs mx-auto"
					style={{ color: C.whiteAlpha75 }}
				>
					Cuidando da sua pele com
					<br />
					<span className="font-semibold" style={{ color: C.white }}>
						excelência e dedicação
					</span>
				</p>

				{/* Diferenciais */}
				<div className="mt-10 space-y-3">
					{[
						{ icon: "ri-shield-check-line", text: "Profissionais especializados" },
						{ icon: "ri-heart-pulse-line", text: "Atendimento personalizado" },
						{ icon: "ri-leaf-line", text: "Tratamentos modernos" },
					].map(({ icon, text }) => (
						<div key={text} className="flex items-center gap-3 justify-center">
							<div
								className="w-7 h-7 rounded-full flex items-center justify-center"
								style={{ backgroundColor: C.primaryAlpha25 }}
							>
								<i className={`${icon} text-sm`} style={{ color: C.primaryLight }} />
							</div>
							<span className="text-sm" style={{ color: C.whiteAlpha75 }}>
								{text}
							</span>
						</div>
					))}
				</div>
			</div>

			<p className="absolute bottom-6 text-xs" style={{ color: C.whiteAlpha35 }}>
				© {new Date().getFullYear()} Centro Derma. Todos os direitos reservados.
			</p>
		</div>
	);

	const ErrorAlert = ({ message }: { message: string }) => (
		<div className="mb-5 flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 p-4">
			<i className="ri-error-warning-fill text-red-500 text-base mt-0.5 flex-shrink-0" />
			<p className="text-red-700 text-sm">{message}</p>
		</div>
	);

	const SubmitButton = ({ label, loadingLabel }: { label: string; loadingLabel: string }) => (
		<button
			type="submit"
			disabled={isLoading}
			className="w-full rounded-xl py-3 text-sm font-semibold text-white transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
			style={{
				background: `linear-gradient(135deg, ${C.primaryLight} 0%, ${C.primary} 50%, #2d7a6b 100%)`,
			}}
		>
			{isLoading ? (
				<>
					<svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
						<circle
							className="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							strokeWidth="4"
						/>
						<path
							className="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
						/>
					</svg>
					{loadingLabel}
				</>
			) : (
				label
			)}
		</button>
	);

	// Tela de recuperação de senha
	if (showForgotPassword) {
		return (
			<div className="min-h-screen flex bg-gray-50">
				<BrandPanel />
				<div className="flex-1 flex items-center justify-center p-8">
					<div className="w-full max-w-md">
						<button
							type="button"
							onClick={() => setShowForgotPassword(false)}
							className="flex items-center gap-2 text-sm text-gray-500 hover:text-brand-600 mb-8 transition-colors"
						>
							<i className="ri-arrow-left-line" /> Voltar ao login
						</button>
						<div className="mb-8">
							<div
								className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
								style={{ backgroundColor: "#e6f4f1" }}
							>
								<i className="ri-mail-send-line text-xl" style={{ color: C.primary }} />
							</div>
							<h2 className="text-2xl font-bold text-gray-900">Recuperar senha</h2>
							<p className="text-gray-500 mt-1.5 text-sm">
								Digite seu email e enviaremos um link de recuperação.
							</p>
						</div>
						<form onSubmit={handleForgotPassword} className="space-y-5" noValidate>
							<div>
								<label
									htmlFor="forgot-email"
									className="block text-sm font-medium text-gray-700 mb-1.5"
								>
									Email
								</label>
								<input
									id="forgot-email"
									name="email"
									type="email"
									autoComplete="email"
									required
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="seu@email.com"
									className={inputClass}
								/>
							</div>
							<SubmitButton label="Enviar link de recuperação" loadingLabel="Enviando..." />
						</form>
					</div>
				</div>
			</div>
		);
	}

	// Tela de cadastro
	if (showRegister) {
		return (
			<div className="min-h-screen flex bg-gray-50">
				<BrandPanel />
				<div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
					<div className="w-full max-w-md py-8">
						<button
							type="button"
							onClick={() => setShowRegister(false)}
							className="flex items-center gap-2 text-sm text-gray-500 hover:text-brand-600 mb-8 transition-colors"
						>
							<i className="ri-arrow-left-line" /> Voltar ao login
						</button>
						<div className="flex lg:hidden justify-center mb-6">
							<img
								src="/logo.jpeg"
								alt="Centro Derma"
								className="w-16 h-16 rounded-xl object-cover shadow-md"
							/>
						</div>
						<div className="mb-8">
							<h2 className="text-2xl font-bold text-gray-900">Criar conta</h2>
							<p className="text-gray-500 mt-1.5 text-sm">
								Preencha os dados para criar seu acesso.
							</p>
						</div>
						{error && <ErrorAlert message={error} />}
						<form onSubmit={handleRegister} className="space-y-4" noValidate>
							<div>
								<label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
									Nome completo
								</label>
								<input
									id="name"
									name="name"
									type="text"
									autoComplete="name"
									required
									placeholder="Seu nome completo"
									className={inputClass}
								/>
							</div>
							<div>
								<label
									htmlFor="reg-email"
									className="block text-sm font-medium text-gray-700 mb-1.5"
								>
									Email
								</label>
								<input
									id="reg-email"
									name="email"
									type="email"
									autoComplete="email"
									required
									placeholder="seu@email.com"
									className={inputClass}
								/>
							</div>
							<div>
								<label
									htmlFor="reg-password"
									className="block text-sm font-medium text-gray-700 mb-1.5"
								>
									Senha
								</label>
								<div className="relative">
									<input
										id="reg-password"
										name="password"
										type={showRegisterPassword ? "text" : "password"}
										autoComplete="new-password"
										required
										placeholder="Mínimo 6 caracteres"
										className={`${inputClass} pr-11`}
									/>
									<button
										type="button"
										onClick={() => setShowRegisterPassword(!showRegisterPassword)}
										className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-brand-500 transition-colors"
										aria-label={showRegisterPassword ? "Ocultar senha" : "Mostrar senha"}
									>
										{showRegisterPassword ? (
											<EyeOff className="h-4 w-4" />
										) : (
											<Eye className="h-4 w-4" />
										)}
									</button>
								</div>
							</div>
							<div>
								<label
									htmlFor="confirmPassword"
									className="block text-sm font-medium text-gray-700 mb-1.5"
								>
									Confirmar senha
								</label>
								<div className="relative">
									<input
										id="confirmPassword"
										name="confirmPassword"
										type={showConfirmPassword ? "text" : "password"}
										autoComplete="new-password"
										required
										placeholder="Repita a senha"
										className={`${inputClass} pr-11`}
									/>
									<button
										type="button"
										onClick={() => setShowConfirmPassword(!showConfirmPassword)}
										className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-brand-500 transition-colors"
										aria-label={showConfirmPassword ? "Ocultar senha" : "Mostrar senha"}
									>
										{showConfirmPassword ? (
											<EyeOff className="h-4 w-4" />
										) : (
											<Eye className="h-4 w-4" />
										)}
									</button>
								</div>
							</div>
							<label className="flex items-start gap-3 cursor-pointer select-none pt-1">
								<input
									id={termsId}
									name="terms"
									type="checkbox"
									required
									className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-brand-500"
								/>
								<span className="text-sm text-gray-600">
									Concordo com os{" "}
									<Link
										to="/terms"
										className="underline underline-offset-2 hover:opacity-80"
										style={{ color: C.primary }}
									>
										termos de serviço
									</Link>
								</span>
							</label>
							<div className="pt-1">
								<SubmitButton label="Criar conta" loadingLabel="Criando..." />
							</div>
						</form>
						<p className="mt-6 text-center text-sm text-gray-500">
							Já tem uma conta?{" "}
							<button
								type="button"
								onClick={() => setShowRegister(false)}
								className="font-medium hover:opacity-80 transition-opacity"
								style={{ color: C.primary }}
							>
								Entrar
							</button>
						</p>
					</div>
				</div>
			</div>
		);
	}

	// Tela principal de login
	return (
		<div className="min-h-screen flex bg-gray-50">
			<BrandPanel />
			<div className="flex-1 flex items-center justify-center p-8">
				<div className="w-full max-w-md">
					{/* Logo mobile */}
					<div className="flex lg:hidden flex-col items-center mb-8">
						<img
							src="/logo.jpeg"
							alt="Centro Derma"
							className="w-20 h-20 rounded-2xl object-contain bg-white shadow-md p-1 mb-3"
						/>
						<h1 className="text-xl font-bold text-gray-900">Centro Derma</h1>
						<p className="text-xs uppercase tracking-widest mt-0.5" style={{ color: C.primary }}>
							Dermatologia & Estética
						</p>
					</div>

					<div className="mb-8">
						<h2 className="text-2xl font-bold text-gray-900">Bem-vindo de volta</h2>
						<p className="text-gray-500 mt-1.5 text-sm">
							Entre com suas credenciais para acessar o sistema.
						</p>
					</div>

					{successMessage && (
						<div className="mb-5 flex items-start gap-3 rounded-xl bg-brand-50 border border-brand-200 p-4">
							<i
								className="ri-checkbox-circle-fill text-base mt-0.5 flex-shrink-0"
								style={{ color: C.primary }}
							/>
							<p className="text-sm" style={{ color: "#0f5a4e" }}>
								{successMessage}
							</p>
						</div>
					)}
					{error && <ErrorAlert message={error} />}

					<form onSubmit={handleSubmit} className="space-y-5" noValidate>
						<div>
							<label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
								Email
							</label>
							<input
								id="email"
								name="email"
								type="email"
								autoComplete="email"
								required
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="seu@email.com"
								className={inputClass}
							/>
						</div>
						<div>
							<div className="flex items-center justify-between mb-1.5">
								<label htmlFor="password" className="block text-sm font-medium text-gray-700">
									Senha
								</label>
								<button
									type="button"
									onClick={() => setShowForgotPassword(true)}
									className="text-xs hover:opacity-80 transition-opacity"
									style={{ color: C.primary }}
								>
									Esqueci minha senha
								</button>
							</div>
							<div className="relative">
								<input
									id="password"
									name="password"
									type={showPassword ? "text" : "password"}
									autoComplete="current-password"
									required
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									placeholder="••••••••"
									className={`${inputClass} pr-11`}
								/>
								<button
									type="button"
									onClick={() => setShowPassword(!showPassword)}
									className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-brand-500 transition-colors"
									aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
								>
									{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
								</button>
							</div>
						</div>
						<div className="pt-1">
							<SubmitButton label="Entrar" loadingLabel="Entrando..." />
						</div>
					</form>

					<div className="mt-6 flex items-center gap-3">
						<div className="flex-1 h-px bg-gray-200" />
						<span className="text-xs text-gray-400">ou</span>
						<div className="flex-1 h-px bg-gray-200" />
					</div>

					<p className="mt-5 text-center text-sm text-gray-500">
						Não tem uma conta?{" "}
						<button
							type="button"
							onClick={() => {
								setError("");
								setShowRegister(true);
							}}
							className="font-medium hover:opacity-80 transition-opacity"
							style={{ color: C.primary }}
						>
							Criar conta
						</button>
					</p>

					<div className="mt-10 flex items-center justify-center gap-4">
						<Link
							to="/terms"
							className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
						>
							Termos de uso
						</Link>
						<span className="text-gray-300">•</span>
						<Link
							to="/privacy"
							className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
						>
							Privacidade
						</Link>
					</div>
				</div>
			</div>
		</div>
	);
}
